import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * FEC-013 — persistent, prominent treasurer-facing disclosure that Schedule D
 * (itemized debts & unpaid obligations) is NOT generated in v1 and must be
 * filed by the treasurer directly in the FEC portal.
 *
 * WHY this is surfacing-only (alert-don't-block, no computation): every
 * `Expenditure` in the model is bank-line-derived = already paid, so there is
 * no unpaid-`Expenditure` concept to itemize a debt from; the one debt the app
 * does track — a candidate `Loan` — is already emitted as summary line 10
 * (Σ OutstandingBalance) and Schedule C (FEC-009). Adding unpaid-obligation
 * tracking purely to fire a deferred-schedule nudge would be over-engineering
 * (see the builder's Schedule-D deferral seam in
 * `FederalFecFileSubmissionBuilder.BuildForm3Records` and `FecF3SummaryRollup`
 * line 10). So a committee with period-end debts/unpaid bills is legally
 * responsible for its own Schedule D, and the generated `.fec` omits it — this
 * disclosure makes that omission LOUD rather than silent.
 *
 * WHO renders it: the federal filing/preview surface only (mounted by
 * `FilePreflightModal` when `preview.stateCode === 'federal'`). Non-federal
 * (AL/GA/LA/VA) paths never see it — Schedule D is an FEC concept.
 *
 * Persistent by design: NO dismiss control (only a learn-more toggle), so it
 * cannot be permanently hidden the way a dismissible toast could.
 */
export function FecScheduleDDeferredDisclosure() {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm"
      data-testid="fec-schedule-d-disclosure"
      role="alert"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-900" />
          <div className="space-y-1">
            <p className="font-medium text-amber-900">
              File Schedule D yourself in the FEC portal
            </p>
            <p className="text-amber-900">
              If your committee has period-end debts or unpaid bills, you must file
              Schedule D directly in the FEC portal — this app does not generate
              Schedule D in this version.
            </p>
            {/* The summary line-10 cover total is candidate-loan-only, so a
                committee with non-loan obligations has an understated line 10
                too — not just a missing itemized Schedule D. State that up front
                so a treasurer who never expands "Learn more" still corrects both
                in the portal, rather than amending only Schedule D and leaving
                the line-10 total wrong. */}
            <p className="text-amber-900">
              Your summary line 10 total — the debts your committee owes — is also
              incomplete when you have non-loan obligations such as unpaid vendor
              invoices. It currently includes your candidate loan balance only, so
              correct the line 10 figure in the portal too — not just the itemized
              Schedule D.
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0 text-amber-900 hover:bg-amber-100"
          onClick={() => setExpanded((v) => !v)}
          data-testid="fec-schedule-d-learn-more"
          aria-expanded={expanded}
        >
          {expanded ? (
            <>
              Less <ChevronUp className="ml-1 h-3 w-3" />
            </>
          ) : (
            <>
              Learn more <ChevronDown className="ml-1 h-3 w-3" />
            </>
          )}
        </Button>
      </div>

      {expanded && (
        <div
          className="space-y-2 border-t border-amber-200 pt-2 text-xs text-amber-900"
          data-testid="fec-schedule-d-details"
        >
          <p>
            <span className="font-medium">What Schedule D covers:</span> itemized debts
            and obligations your committee owes at the end of the reporting period —
            unpaid bills, outstanding vendor invoices, and similar obligations that are
            not yet settled.
          </p>
          <p>
            <span className="font-medium">What this app files:</span> your candidate loan
            balance only. Summary line 10 (total debts owed by your committee) and Schedule
            C report your candidate loan and nothing else — non-loan debts such as unpaid
            vendor invoices and bills are not included. So when your committee owes those,
            both the line 10 total and the <span className="italic">itemized</span> Schedule
            D are incomplete in the generated file.
          </p>
          <p>
            <span className="font-medium">What you must do:</span> if you have any period-end
            unpaid obligations, log in to the FEC portal after you upload the generated file
            and (1) add Schedule D for the itemized debts and (2) correct the line 10 total
            so it includes those non-loan obligations. The uploaded file includes neither.
          </p>
          <p className="text-amber-800">
            Full debt reporting — itemized Schedule D and a complete line 10 total — is
            planned for a future version.
          </p>
        </div>
      )}
    </div>
  );
}
